<template>
  <z-form ref="form" class="w-100">
    <base-modal>
      <z-form-group class="row">
        <div class="col-3 text-right">
          {{ $t('autocaller_v2.task.field.number') }}
        </div>
        <z-form-input class="col-8" :rules="{ required: true, min: 9, max: 18 }">
          <tel-input v-model="form.number" :disabled="number_disabled" />
        </z-form-input>
      </z-form-group>
      <z-form-group class="row">
        <div class="col-3 text-right">
          {{ $t('autocaller_v2.task.field.caller_id') }}
        </div>
        <z-form-input class="col-8 align-items-center">
          <select-caller-id v-model="form.caller_id" :multiple="false" />

          <tooltip
            class="ml-3 w-250 pos-a"
            placement="top"
            min-width="300"
            max-width="300"
            theme="light"
            :content="$t('autocaller_v2.task.tooltips.caller_id')"
          >
            <icon size="18" type="ios-help-circle-outline" />
          </tooltip>
        </z-form-input>
      </z-form-group>
      <z-form-group class="row">
        <div class="col-3 text-right">
          {{ $t('autocaller_v2.task.field.strategy') }}
        </div>
        <z-form-input class="col" :rules="{ min_value: 0, max_value: 12 }">
          <input-number
            v-model="form.attempts"
            :min="0"
            :disabled="attempts_disabled"
            @on-blur="setDefaultAttempts"
          />
          <tooltip
            class="ml-3 w-250"
            placement="top"
            min-width="300"
            max-width="300"
            theme="light"
            :content="$t('autocaller_v2.task.tooltips.strategy')"
          >
            <icon size="18" type="ios-help-circle-outline" />
          </tooltip>
        </z-form-input>
      </z-form-group>
      <z-form-group class="row">
        <div class="col-3 text-right">
          {{ $t('autocaller_v2.task.field.priority') }}
        </div>
        <z-form-input class="col">
          <input-number
            v-model="form.priority"
            :min="1"
            :disabled="priority_disabled"
            @on-blur="setDefaultPriority"
          />
        </z-form-input>
      </z-form-group>
      <z-form-group class="row">
        <div class="col-3 text-right">
          {{ $t('autocaller_v2.task.field.start') }}
        </div>
        <z-form-input class="col">
          <date-picker v-model="form.start" type="datetime" :disabled="start_disabled" />
        </z-form-input>
      </z-form-group>
      <z-form-group class="row align-items-center">
        <span class="col-3 text-right">
          {{ $t('autocaller_v2.task.field.delayed_until') }}
        </span>
        <checkbox v-model="is_delay_send" class="ml-3" @on-change="get_current_date" />

        <z-form-input :class="{ col: true }" :rules="{ old_date: is_delay_send ? 0 : false }">
          <date-picker
            v-show="is_delay_send"
            v-model="form.delayed_start"
            type="datetime"
            :options="dateOptions"
            :clearable="false"
          />
          <tooltip
            v-if="is_delay_send"
            class="ml-3"
            placement="top"
            min-width="200"
            max-width="200"
            theme="light"
            :content="$t('autocaller_v2.task.tooltips.delayed_until')"
          >
            <icon size="18" type="ios-help-circle-outline" />
          </tooltip>
        </z-form-input>
      </z-form-group>

      <z-form-group class="row">
        <div class="col-3 text-right">
          {{ $t('autocaller_v2.products.title') }}
        </div>

        <z-form-input class="col-8">
          <select-products
            v-model="form.product_name"
            :value="form.product_name"
            :loading="is_products_list_loading"
            :list="products"
            @handle_clear="clear_product"
            @handle_select="select_product"
            @handle_create="add_product"
          />
          <div v-if="!use_skills" class="form-input__warning form-input__warn--absolute"> {{ $t('autocaller_v2.task.warnings.skills') }} </div>

          <tooltip
            class="ml-3 w-250 pos-a"
            placement="top"
            min-width="300"
            max-width="300"
            theme="light"
            :content="$t('autocaller_v2.products.tooltips.usage')"
          >
            <icon size="18" type="ios-help-circle-outline" />
          </tooltip>
        </z-form-input>
      </z-form-group>
      <z-form-group class="row">
        <div class="col-3 text-right">
          {{ $t('autocaller_v2.task.field.additionally') }}
        </div>
        <z-form-input class="col-8" :rules="{ max: 255 }">
          <i-input v-model="form.client_data" :clearable="true" />
          <tooltip
            class="ml-3 w-250 pos-a"
            placement="top"
            min-width="200"
            max-width="200"
            theme="light"
            :content="$t('autocaller_v2.task.tooltips.additionally')"
          >
            <icon size="18" type="ios-help-circle-outline" />
          </tooltip>
        </z-form-input>
      </z-form-group>

      <i-button
        v-if="is_edit"
        slot="footer-ok"
        size="large"
        type="success"
        :loading="is_button_loading"
        @click="save_edit"
      >
        {{ $t('shared.component.button.save') }}
      </i-button>
      <i-button
        v-else
        slot="footer-ok"
        size="large"
        type="success"
        :loading="is_button_loading"
        @click="save"
      >
        {{ $t('shared.component.button.save') }}
      </i-button>
    </base-modal>
  </z-form>
</template>

<script>
  import BaseModal from '@shared/components/modal/modal';
  import SelectCallerId from './campaigns-input-caller-id';
  import SelectProducts from './selects/products';
  import TelInput from '@shared/components/input/input-phone-number';
  import { toBackendDateWithTime } from '@shared/helpers/date_format';
  import { TaskService } from '@autocaller_v2/services/TaskService';
  import { ProductsService } from '@autocaller_v2/services/ProductsService';
  import moment from 'moment-es6';

  export default {
    components: { BaseModal, TelInput, SelectCallerId, SelectProducts },

    props: {
      is_edit: {
        type: Boolean,
        default: false
      },
      id: {
        type: [Number, String],
        default: null
      },
      campaign_id: {
        type: [Number, String],
        required: true
      },
      item: {
        type: Object,
        default: () => ({})
      },
      use_skills: {
        type: Boolean,
        default: false
      },
      number_disabled: {
        type: Boolean,
        default: false
      },
      priority_disabled: {
        type: Boolean,
        default: false
      },
      attempts_disabled: {
        type: Boolean,
        default: false
      },
      start_disabled: {
        type: Boolean,
        default: false
      }
    },
    data: () => ({
      is_delay_send: false,
      form: {
        priority: 1,
        attempts: 0,
        start: null,
        number: null,
        caller_id: null,
        delayed_start: '',
        product_id: null,
        product_name: '',
        client_data: ''
      },
      products: [],
      is_products_list_loading: false,
      is_button_loading: false,
      dateOptions: {
        disabledDate(date) {
          return date && date.valueOf() < Date.now() - 86400000;
        }
      }
    }),
    watch: {
      item: {
        handler: function () {
          this.form = {
            caller_id: this.item.caller_id,
            delayed_start: this.item.delayed_until,
            number: this.item.number,
            priority: this.item.priority,
            attempts: this.item.attempts,
            start: this.item.lead_date,
            product_id: this.item.product_id,
            product_name: this.item.product_name,
            client_data: this.item.client_data
          };

          if (this.is_edit) {
            this.form.number = this.item.number ? `+${this.item.number}` : '';
          }
        },
        immediate: true,
        deep: true
      }
    },
    created() {
      this.is_products_list_loading = true;
      ProductsService.getProducts()
        .then((response) => {
          this.products = response;
          this.form.product_name = response?.find((i) => i.id === this.form.product_id)?.name || '';
        })
        .catch(() => this.$noty.error(this.$t('autocaller_v2.operators.error.create_project')))
        .finally(() => (this.is_products_list_loading = false));
    },
    methods: {
      select_product(value) {
        this.form.product_name = value.label;
      },
      clear_product() {
        this.form.product_name = '';
      },
      add_product(value) {
        this.is_products_list_loading = true;

        return ProductsService.createItem({ name: value.replace(/^\s+|\s+$/g, '') })
          .then((response) => {
            const { id, name } = response;
            this.products = [...this.products, { id, name }];
            this.form.product_name = name;
          })
          .finally(() => (this.is_products_list_loading = false));
      },
      get_current_date() {
        if (this.is_delay_send) {
          this.form.delayed_start = moment().utc().add(1, 'hours').format('YYYY-MM-DD HH:mm:ss');
        } else {
          this.form.delayed_start = null;
        }
      },
      setDefaultAttempts() {
        if (this.form.attempts == null) {
          this.form.attempts = 0;
        }
      },
      setDefaultPriority() {
        if (!this.form.priority) {
          this.form.priority = 1;
        }
      },
      save_edit() {
        const { caller_id, delayed_start, client_data } = this.form;
        const delayed_until = delayed_start ? toBackendDateWithTime(delayed_start) : undefined;

        const params = {
          caller_id: caller_id ? caller_id.replace(/\+/g, '') : '',
          delayed_until,
          product_id: this.products?.find((i) => i.name === this.form.product_name)?.id || null,
          client_data
        };

        this.$refs.form.validate().then((is_valid) => {
          if (is_valid) {
            TaskService.editItem(this.campaign_id, this.id, params).then((res) => {
              this.$emit('success', res);
            });
          }
        });
      },
      save() {
        const {
          number,
          caller_id,
          priority,
          attempts,
          start,
          delayed_start,
          client_data
        } = this.form;
        const lead_date = start ? toBackendDateWithTime(start) : '';
        const delayed_until = delayed_start ? toBackendDateWithTime(delayed_start) : '';

        const params = {
          tasks: [
            {
              number: number ? number.replace(/\+/g, '') : '',
              caller_id: caller_id ? caller_id.replace(/\+/g, '') : '',
              priority,
              attempts,
              lead_date,
              delayed_until,
              product_id: this.products?.find((i) => i.name === this.form.product_name)?.id || null,
              client_data
            }
          ]
        };
        if (params.tasks[0].lead_date == '') {
          delete params.tasks[0].lead_date;
        }
        if (params.tasks[0].delayed_until == '') {
          delete params.tasks[0].delayed_until;
        }

        this.$refs.form.validate().then((is_valid) => {
          if (is_valid) {
            TaskService.createItem(this.campaign_id, params).then((res) => {
              this.$emit('success', res);
            });
          }
        });
      }
    }
  };
</script>

<style lang="scss">
  .w-250 {
    .ivu-tooltip-inner {
      min-width: 250px;
    }
  }
  .pos-a {
    position: absolute;
    top: 5px;
    right: -15px;
  }

  .vue-tel-input.disabled {
    cursor: not-allowed;
    background-color: #f3f3f3;
    color: #ccc;
    pointer-events: none;

    &:hover {
      border: 1px solid #dcdee2 !important;
      cursor: not-allowed;
    }
  }
</style>
